/**
 * The gun registry: one import and one entry per gun. Every stat lives in the gun's own file under
 * `guns/`.
 */

import { AK47 } from '@/sim/weapons/guns/ak47';
import { BENELLI_M4 } from '@/sim/weapons/guns/benelliM4';
import { GLOCK17 } from '@/sim/weapons/guns/glock17';
import { M1911 } from '@/sim/weapons/guns/m1911';
import { M249 } from '@/sim/weapons/guns/m249';
import { M4A1 } from '@/sim/weapons/guns/m4a1';
import { M82 } from '@/sim/weapons/guns/m82';
import { MP5 } from '@/sim/weapons/guns/mp5';
import { REMINGTON700 } from '@/sim/weapons/guns/remington700';
import { REMINGTON870 } from '@/sim/weapons/guns/remington870';
import { UZI } from '@/sim/weapons/guns/uzi';
import type { WeaponId, WeaponSpec } from '@/sim/weapons/weaponContract';

export const WEAPONS = {
  glock17: GLOCK17,
  m1911: M1911,
  mp5: MP5,
  uzi: UZI,
  m4a1: M4A1,
  ak47: AK47,
  remington870: REMINGTON870,
  benelliM4: BENELLI_M4,
  remington700: REMINGTON700,
  m82: M82,
  m249: M249,
} as const satisfies Record<WeaponId, WeaponSpec>;

/**
 * Rack order, grouped by class. Also the wire's vocabulary: `weaponIndex` is a position in this
 * list, so inserting or reordering is a protocol change.
 */
export const WEAPON_ORDER: readonly WeaponId[] = [
  'glock17',
  'm1911',
  'mp5',
  'uzi',
  'm4a1',
  'ak47',
  'remington870',
  'benelliM4',
  'remington700',
  'm82',
  'm249',
];

/** What a shooter starts with when nothing chose otherwise. */
export const DEFAULT_WEAPON: WeaponId = 'glock17';

/** Spec lookup, total over the union so it never widens to `undefined`. */
export function weaponSpec(id: WeaponId): WeaponSpec {
  return WEAPONS[id];
}

/** Whether `value` names a weapon. Used when mapping raw strings off a URL or a save. */
export function isWeaponId(value: string): value is WeaponId {
  return Object.hasOwn(WEAPONS, value);
}

/**
 * A stable 1-based index for a weapon, in `WEAPON_ORDER`. The wire carries this rather than the id
 * string. Index 0 means "no change" on an input frame, so this never returns it.
 */
export function weaponIndex(id: WeaponId): number {
  return WEAPON_ORDER.indexOf(id) + 1;
}

/** The inverse of `weaponIndex`. `null` for 0 or an index past the end of `WEAPON_ORDER`. */
export function weaponByIndex(index: number): WeaponId | null {
  return WEAPON_ORDER[index - 1] ?? null;
}
